<template>
  <section>
    <div style="margin: 4px 0 16px">
      <h2 class="guests-page__title" style="font-size: 18px">
        Occupazione e rendimento
      </h2>
      <p class="guests-page__subtitle">
        Analisi camere, ospiti presenti e rendimento mensile della struttura
      </p>
    </div>

    <div v-if="loading" class="guests-loading">Caricamento…</div>

    <template v-else-if="analytics">
      <!-- Insight principali -->
      <div class="dash-insights">
        <div class="dash-card">
          <div class="dash-card__value">
            {{ summary.occupiedBeds }} / {{ summary.totalBeds }}
          </div>
          <div class="dash-card__label">Occupazione</div>
          <div class="dash-card__hint">
            {{ summary.occupancyRate }}% occupato
          </div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">
            {{ euro(summary.currentMonthlyRevenue) }}
          </div>
          <div class="dash-card__label">Rendita mensile attuale</div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">
            {{ euro(summary.potentialMonthlyRevenue) }}
          </div>
          <div class="dash-card__label">Rendita mensile potenziale</div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">{{ euro(summary.revenueGap) }}</div>
          <div class="dash-card__label">Gap potenziale</div>
        </div>
      </div>

      <p class="occ-hint-op">
        <i class="fa-solid fa-circle-info"></i> Questa è una vista analitica.
        Per la gestione operativa dei letti (assegna / libera) usa la tab
        <strong>Camere</strong>.
      </p>

      <!-- Lista camere + ospiti presenti -->
      <h3 class="guests-page__title occ-section-title">
        Camere e ospiti presenti
      </h3>
      <div v-if="rooms.length === 0" class="guests-loading">
        Nessuna camera attiva in questa struttura.
      </div>
      <div v-else class="occ-grid">
        <div v-for="room in rooms" :key="room.roomId" class="occ-room">
          <div class="occ-room__head">
            <span class="occ-room__name">{{ room.roomName }}</span>
            <span class="occ-room__count">
              {{ room.occupiedBeds }} / {{ room.totalBeds }} posti ·
              {{ room.occupancyRate }}%
            </span>
          </div>

          <div v-if="room.guests.length === 0" class="beds-empty">
            Nessun ospite presente.
          </div>
          <div v-for="g in room.guests" :key="g.guestId" class="occ-guest-row">
            <span>
              {{ g.firstName }} {{ g.lastName }}
              <small class="occ-muted">· Letto {{ g.bedCode }}</small>
            </span>
            <span class="occ-fee">{{ feeLabel(g.monthlyFee) }}</span>
          </div>

          <div class="occ-room__foot">
            <span
              ><strong>Rendimento:</strong>
              {{ euro(room.currentMonthlyRevenue) }}/mese</span
            >
            <span class="occ-muted">
              Potenziale {{ euro(room.potentialMonthlyRevenue) }} · Gap
              {{ euro(room.revenueGap) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Classifica rendimento stanze -->
      <h3 class="guests-page__title occ-section-title">
        Classifica rendimento stanze
      </h3>
      <div v-if="ranking.length === 0" class="guests-loading">
        Nessuna camera da classificare.
      </div>
      <div v-else class="guests-table-wrap">
        <table class="guests-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Camera</th>
              <th>Rendimento mensile</th>
              <th>Occupazione</th>
              <th>Posti</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in ranking" :key="r.roomId">
              <td>{{ i + 1 }}</td>
              <td>{{ r.roomName }}</td>
              <td>{{ euro(r.currentMonthlyRevenue) }}/mese</td>
              <td>{{ r.occupancyRate }}%</td>
              <td>{{ r.occupiedBeds }} / {{ r.totalBeds }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Storico mensile -->
      <h3 class="guests-page__title occ-section-title">
        Storico rendimento e occupazione
      </h3>
      <div class="occ-chart">
        <div v-for="m in history" :key="m.month" class="occ-chart__col">
          <div class="occ-chart__bar-wrap">
            <div
              class="occ-chart__bar"
              :style="{ height: barHeight(m.occupancyRate) }"
              :title="m.occupancyRate + '% occupazione'"
            ></div>
          </div>
          <div class="occ-chart__rev">
            <template v-if="m.currentMonthlyRevenue != null">
              {{ euroShort(m.currentMonthlyRevenue) }}
            </template>
            <template v-else>—</template>
          </div>
          <div class="occ-chart__occ">{{ m.occupancyRate }}%</div>
          <div class="occ-chart__month">{{ m.month }}</div>
        </div>
      </div>
      <p class="occ-history-note">
        <i class="fa-solid fa-circle-info"></i> {{ historyNote }}
      </p>
    </template>
  </section>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "FacilityOccupancyPanel",
  props: {
    facilityId: { type: [String, Number], required: true },
  },
  data() {
    return { analytics: null, loading: true };
  },
  computed: {
    summary() {
      return (this.analytics && this.analytics.summary) || {};
    },
    rooms() {
      return (this.analytics && this.analytics.rooms) || [];
    },
    ranking() {
      return (this.analytics && this.analytics.roomRanking) || [];
    },
    history() {
      return (this.analytics && this.analytics.monthlyHistory) || [];
    },
    historyNote() {
      return (
        (this.analytics && this.analytics.note) ||
        "Storico in costruzione: i dati mensili saranno più significativi dopo alcuni mesi di utilizzo."
      );
    },
  },
  methods: {
    ...mapActions(["fetchFacilityOccupancyAnalytics"]),
    euro(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    },
    euroShort(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      if (n >= 1000) return `€ ${(n / 1000).toFixed(1)}k`;
      return `€ ${n.toFixed(0)}`;
    },
    feeLabel(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} / mese`;
    },
    barHeight(rate) {
      const r = Math.max(0, Math.min(100, Number(rate) || 0));
      // min 4% così la colonna resta visibile anche a 0.
      return `${Math.max(4, r)}%`;
    },
    async load() {
      this.loading = true;
      try {
        this.analytics = await this.fetchFacilityOccupancyAnalytics(
          this.facilityId
        );
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error ||
            "Errore nel caricamento delle analisi di occupazione."
        );
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/RoomsPage.css" />
<style src="@/css/AppLayout.css" />
